import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {MultiValueFilter} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

import {RequestV2DefinitionFilterFormQuery} from './__generated/RequestV2DefinitionFilterFormQuery';
import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import useFilterArchivedDropdownOptions, {
  ArchivableOptionData,
} from './base/useFilterArchivedDropdownOptions';

type RequestV2Definition = ExtractByTypename<
  RequestV2DefinitionFilterFormQuery['allRequestV2Definitions'][number],
  'RequestV2DefinitionNode'
>;

export const REQUEST_V2_DEFINITION_FILTER_FORM_QUERY: DocumentNode = gql`
  query RequestV2DefinitionFilterFormQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    allRequestV2Definitions {
      id
      name
      archiveData {
        archived
      }
    }
  }
`;

interface RequestV2DefinitionFilterFormProps extends BaseFilterFormProps {
  filter: MultiValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: MultiValueFilter) => void;
  onRemove?: () => void;
}

function _buildOptionDatas(requestV2Definitions: Array<RequestV2Definition>): Array<ArchivableOptionData> {
  return requestV2Definitions.map((requestV2Definition) => ({
    name: requestV2Definition.name,
    value: requestV2Definition.id,
    isArchived: requestV2Definition.archiveData.archived,
  }));
}

function RequestV2DefinitionFilterForm(props: RequestV2DefinitionFilterFormProps): JSX.Element {
  const {filter, onChangeFilter, filterStyle, onRemove} = props;
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const onChangeValueBase = (value: Array<string>): void => {
    onChangeFilter({...filter, value});
  };
  const filterArchivedDropdownOptionProps = useFilterArchivedDropdownOptions(
    [],
    'Show archived schemas',
    onChangeValueBase
  );
  return (
    <BenchlingQuery<RequestV2DefinitionFilterFormQuery>
      query={REQUEST_V2_DEFINITION_FILTER_FORM_QUERY}
      renderLoading={() => (
        <DropdownFilterForm
          {...skeletonProps}
          {...filterArchivedDropdownOptionProps}
          dropdownToggleText={prefixedToggleText('Request type', [], filter)}
          filterStyle={filterStyle}
          isMulti={true}
          isSearchable={true}
          onRemove={onRemove}
          value={filter.value}
        />
      )}
    >
      {({allRequestV2Definitions: requestV2Definitions}) => {
        const optionDatas = _buildOptionDatas(requestV2Definitions);
        return (
          <DropdownFilterForm
            {...skeletonProps}
            {...filterArchivedDropdownOptionProps}
            optionDatas={optionDatas}
            dropdownToggleText={prefixedToggleText('Request type', optionDatas, filter)}
            filterStyle={filterStyle}
            isMulti={true}
            isSearchable={true}
            onRemove={onRemove}
            value={filter.value}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export default RequestV2DefinitionFilterForm;
